import { Stethoscope, CheckCircle2 } from "lucide-react";
import {
  Container,
  Section,
  Heading,
  Lede,
  Eyebrow,
  Card,
  LinkButton,
} from "../../components/ui";
import { Seo } from "../../lib/head";

const RULES = [
  "Anesthesia high-alert med protocols",
  "DEA controlled substance perpetual inventory",
  "Sedation level documentation",
  "Vaccine storage temperature monitoring",
  "Look-alike / sound-alike (LASA) segregation",
  "Multi-dose vial expiry tracking",
  "Beyond-use date (BUD) compliance",
  "Compounding USP <797> oversight",
];

export function AscSolution() {
  return (
    <>
      <Seo title="ASC Pharmacy Compliance Software | WeConsult Rx" description="ASC pharmacy compliance software, AAAHC-aligned. DEA, high-alert meds, sedation, vaccine storage, and USP <797> compounding oversight for ambulatory surgical centers — codified." path="/solutions/asc" />
      <Section className="pb-8">
        <Container>
          <div className="grid gap-12 lg:grid-cols-[1.4fr_1fr] lg:items-center">
            <div>
              <Eyebrow>Ambulatory surgical</Eyebrow>
              <Heading level={1} className="mt-3">
                AAAHC pharmaceutical standards. Coded into your workflow.
              </Heading>
              <Lede className="mt-6">
                Built for consultant pharmacists supporting ambulatory surgical
                centers under AAAHC accreditation and CMS conditions for
                coverage.
              </Lede>
              <div className="mt-8 flex gap-3">
                <LinkButton to="/demo">Book an ASC demo</LinkButton>
                <LinkButton to="/features" variant="secondary">
                  See all features
                </LinkButton>
              </div>
            </div>
            <Card className="bg-forest-gradient text-white border-transparent">
              <Stethoscope className="h-10 w-10 mb-4" aria-hidden />
              <p className="text-sm uppercase tracking-tagline font-semibold text-white/85">
                AAAHC-aligned
              </p>
              <p className="mt-3 text-2xl font-bold">
                ASC pharmacy compliance checks
              </p>
              <p className="mt-4 text-white/85 text-sm">
                DEA, high-alert medication, vaccine cold chain, and compounding
                oversight — every check anchored to the citation.
              </p>
            </Card>
          </div>
        </Container>
      </Section>

      <Section tone="soft">
        <Container>
          <div className="max-w-2xl">
            <Eyebrow>Rule coverage</Eyebrow>
            <Heading level={2} className="mt-2">
              Coverage that your surveyor will respect.
            </Heading>
          </div>
          <ul className="mt-10 grid gap-4 md:grid-cols-2">
            {RULES.map((r) => (
              <li
                key={r}
                className="flex items-start gap-3 rounded-xl border border-border bg-surface p-4"
              >
                <CheckCircle2 className="h-5 w-5 text-forest-700 mt-0.5" aria-hidden />
                <span className="text-sm text-ink-800 font-medium">{r}</span>
              </li>
            ))}
          </ul>
        </Container>
      </Section>

      <Section>
        <Container>
          <div className="grid gap-10 md:grid-cols-3">
            <div>
              <Heading level={3}>AAAHC alignment</Heading>
              <p className="mt-3 text-ink-600">
                Pharmaceutical services standards mapped to a rule pack with a
                citation for every check.
              </p>
            </div>
            <div>
              <Heading level={3}>DEA + USP</Heading>
              <p className="mt-3 text-ink-600">
                Perpetual inventory, DEA Form 222, USP &lt;797&gt;
                compounding oversight, look-alike / sound-alike segregation.
              </p>
            </div>
            <div>
              <Heading level={3}>State filings</Heading>
              <p className="mt-3 text-ink-600">
                FL AHCA-ASC + GA DCH-ASC forms supported. Automated renewal
                reminders at 90, 30, and expired intervals.
              </p>
            </div>
          </div>
        </Container>
      </Section>
    </>
  );
}
